"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  HiAnnotation,
  HiClipboardCopy,
  HiCheck,
  HiQuestionMarkCircle,
  HiRefresh,
} from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const MAX_TWEET_LENGTH = 280;

const FAQ_ITEMS = [
  {
    question: "How does the thread splitter decide where to break tweets?",
    answer:
      "The formatter splits text at sentence boundaries (periods, exclamation marks, question marks) so each tweet reads naturally. If a single sentence exceeds 280 characters, it falls back to splitting at the nearest word boundary.",
  },
  {
    question: "Does the numbering count toward the 280-character limit?",
    answer:
      "Yes. The numbering label (e.g. '1/5 ') is included when calculating whether each tweet fits within 280 characters, so every exported tweet is safe to post directly.",
  },
  {
    question: "Can I copy individual tweets?",
    answer:
      "Yes. Each tweet card has its own copy button so you can paste them one at a time, or use the Copy All button to get the full thread in a single clipboard action.",
  },
  {
    question: "Is there a limit on how much text I can paste in?",
    answer:
      "There is no hard limit enforced by the tool. Very long inputs will produce more tweet cards. The processing happens entirely in your browser, so no text is sent to a server.",
  },
  {
    question: "Why use this instead of just writing directly on Twitter?",
    answer:
      "Writing offline gives you full control over pacing, editing, and structure before anything is published. You can draft, rearrange, and refine the full thread, then paste each tweet in sequence.",
  },
];

function splitIntoTweets(text: string): string[] {
  if (!text.trim()) return [];

  const sentenceRegex = /[^.!?]+[.!?]*/g;
  const sentences: string[] = [];
  let match;
  while ((match = sentenceRegex.exec(text)) !== null) {
    const s = match[0].trim();
    if (s) sentences.push(s);
  }
  if (sentences.length === 0) sentences.push(text.trim());

  const rawTweets: string[] = [];
  let current = "";

  for (const sentence of sentences) {
    const separator = current ? " " : "";
    const candidate = current + separator + sentence;

    // Reserve 6 chars for numbering prefix "99/99 "
    if (candidate.length <= MAX_TWEET_LENGTH - 6) {
      current = candidate;
    } else {
      if (current) rawTweets.push(current.trim());
      if (sentence.length > MAX_TWEET_LENGTH - 6) {
        const words = sentence.split(" ");
        let chunk = "";
        for (const word of words) {
          const joined = (chunk + " " + word).trim();
          if (joined.length <= MAX_TWEET_LENGTH - 6) {
            chunk = joined;
          } else {
            if (chunk) rawTweets.push(chunk);
            chunk = word;
          }
        }
        current = chunk;
      } else {
        current = sentence;
      }
    }
  }
  if (current.trim()) rawTweets.push(current.trim());

  return rawTweets.map((t, i) => `${i + 1}/${rawTweets.length} ${t}`);
}

export default function TwitterThreadFormatterClient() {
  const [text, setText] = useState("");
  const [tweets, setTweets] = useState<string[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const handleFormat = useCallback(() => {
    setTweets(splitIntoTweets(text));
    setCopiedIndex(null);
    setCopiedAll(false);
  }, [text]);

  const handleReset = () => {
    setText("");
    setTweets([]);
    setCopiedIndex(null);
    setCopiedAll(false);
  };

  const copyTweet = async (index: number) => {
    await navigator.clipboard.writeText(tweets[index]);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAll = async () => {
    await navigator.clipboard.writeText(tweets.join("\n\n"));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Twitter Thread Formatter"
        description="Split long text into numbered tweets automatically at sentence boundaries, under 280 characters each."
        url="https://plugmonkey.xyz/tool/twitter-thread-formatter/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Twitter Thread Formatter",
            url: "https://plugmonkey.xyz/tool/twitter-thread-formatter/",
          },
        ]}
        faq={FAQ_ITEMS}
      />

      {/* Hero */}
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-sky-500/10 rounded-full text-sky-500">
          <HiAnnotation className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Twitter Thread Formatter</h1>
          <p className="text-muted-foreground mt-1">
            Turn any long text into a numbered thread in seconds.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Writing a long-form thought, essay, or story for Twitter? Threading by hand is tedious.
          Paste your text below and this tool automatically splits it into numbered tweets, each
          under 280 characters, breaking at natural sentence boundaries so every tweet reads
          cleanly.
        </p>
        <p>
          The numbering (e.g. 1/8) is included in the character count, so every tweet is copy-paste
          ready. Copy tweets individually or grab the whole thread at once.
        </p>
      </div>

      {/* Input */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HiAnnotation /> Your Text
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <textarea
              id="thread-text"
              name="thread-text"
              className="w-full min-h-[200px] rounded-lg border border-input bg-background px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
              placeholder="Paste your text here. The formatter will split it into tweets automatically..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              aria-label="Text to format into tweets"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{text.length} characters</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleReset}>
                  <HiRefresh className="mr-1 h-4 w-4" /> Reset
                </Button>
                <Button onClick={handleFormat} disabled={!text.trim()} size="sm">
                  Format Thread
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Output */}
        {tweets.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">
                Thread Preview{" "}
                <span className="text-muted-foreground font-normal text-base">
                  ({tweets.length} tweets)
                </span>
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={copyAll}
                className="flex items-center gap-1"
              >
                {copiedAll ? (
                  <>
                    <HiCheck className="h-4 w-4 text-green-500" /> Copied!
                  </>
                ) : (
                  <>
                    <HiClipboardCopy className="h-4 w-4" /> Copy All
                  </>
                )}
              </Button>
            </div>

            {tweets.map((tweet, index) => (
              <Card key={index} className="relative">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <p className="flex-1 text-sm leading-relaxed whitespace-pre-wrap">{tweet}</p>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyTweet(index)}
                        aria-label={`Copy tweet ${index + 1}`}
                        className="h-8 w-8 p-0"
                      >
                        {copiedIndex === index ? (
                          <HiCheck className="h-4 w-4 text-green-500" />
                        ) : (
                          <HiClipboardCopy className="h-4 w-4" />
                        )}
                      </Button>
                      <span className="text-xs text-muted-foreground">{tweet.length}/280</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <div className="flex justify-center pt-2">
              <Button variant="outline" onClick={copyAll} className="gap-2">
                {copiedAll ? (
                  <>
                    <HiCheck className="h-4 w-4 text-green-500" /> All Copied!
                  </>
                ) : (
                  <>
                    <HiClipboardCopy className="h-4 w-4" /> Copy Full Thread
                  </>
                )}
              </Button>
            </div>

            <div className="bg-card border rounded-xl p-6 mt-6">
              <h3 className="font-bold text-lg mb-2">Growing on X / Twitter?</h3>
              <p className="text-muted-foreground text-sm mb-4">
                <strong>Followers Exporter Pro for X</strong> lets you export your follower list to
                CSV so you can analyze your audience and re-engage outside the platform.
              </p>
              <Button asChild variant="secondary">
                <Link href="/product/x-followers-exporter-pro/">View Extension</Link>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* FAQ */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={FAQ_ITEMS} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
